import { IdentityUnavailableError } from '../../services/light-http.service';
import { BulkZipExcludedFile, BulkZipJobView } from '../../types/bulk-zip';

/**
 * Review 7 X1-05: what "Download all (ZIP)" will actually build, so the button can say so.
 *
 * The server leaves a file out of a new bulk ZIP when the same owner already holds a still-ready ZIP
 * that contains it (spec rule Z6, confirmed on QA: "Download all · 9 files" built 4 because 5 were in
 * a ready `convert-one-to-one_…_5files` ZIP). The button used to promise all 9. This mirrors Z6 from
 * the jobs this browser already knows (`BulkZipService.jobs$`), and only shapes the COPY -- the request
 * is unchanged, and the server's answer (the job's `excludedFiles`) stays the source of truth on the row.
 */
export interface BulkZipCoveredGroup {
    jobId: string;
    label: string;
    count: number;
}

export interface BulkZipPlan {
    total: number;
    fresh: number;
    coveredCount: number;
    covered: BulkZipCoveredGroup[];
}

/** A job whose ZIP can still be downloaded: the same predicate the Downloads Center row uses for READY, plus expiry. */
export function isReadyZip(job: BulkZipJobView, now: number): boolean {
    if (!job || job.failed || job.processStage !== 'COMPLETED' || !job.active) return false;
    if (job.artifactExpiresAt) {
        const t = Date.parse(job.artifactExpiresAt);
        if (Number.isFinite(t) && t <= now) return false;
    }
    return true;
}

export function bulkZipPlan(fileIds: readonly string[], jobs: readonly BulkZipJobView[] | null | undefined, now: number = Date.now()): BulkZipPlan {
    const ids = Array.from(new Set(fileIds.filter(Boolean)));
    const coveredBy = new Map<string, BulkZipJobView>();
    for (const job of jobs || []) {
        if (!isReadyZip(job, now)) continue;
        for (const id of job.fileIds || []) if (!coveredBy.has(id)) coveredBy.set(id, job);
    }
    const groups = new Map<string, BulkZipCoveredGroup>();
    let coveredCount = 0;
    for (const id of ids) {
        const job = coveredBy.get(id);
        if (!job) continue;
        coveredCount++;
        const g = groups.get(job.jobId) || { jobId: job.jobId, label: job.label, count: 0 };
        g.count++;
        groups.set(job.jobId, g);
    }
    return { total: ids.length, fresh: ids.length - coveredCount, coveredCount, covered: [...groups.values()] };
}

const files = (n: number) => `${n} file${n === 1 ? '' : 's'}`;

/** The button's label. */
export function bulkZipButtonText(plan: BulkZipPlan): string {
    if (plan.coveredCount === 0) return `📦 Download all (ZIP) · ${files(plan.total)}`;
    if (plan.fresh === 0) return `📦 All ${files(plan.total)} are already in a ready ZIP`;
    return `📦 ZIP the ${plan.fresh} new file${plan.fresh === 1 ? '' : 's'} · ${plan.coveredCount} already in a ready ZIP`;
}

/** The line under the button saying where the rest are, or null when nothing is left out. */
export function bulkZipCoveredNote(plan: BulkZipPlan): string | null {
    if (plan.coveredCount === 0) return null;
    const where = plan.covered.map(g => `${g.label}.zip (${files(g.count)})`).join(', ');
    return plan.fresh === 0
        ? `They are in ${where}, still ready in your Downloads, so no new ZIP is needed.`
        : `${plan.coveredCount === 1 ? '1 file is' : `${plan.coveredCount} files are`} already in ${where}, still ready in your Downloads, so the new ZIP leaves ${plan.coveredCount === 1 ? 'it' : 'them'} out.`;
}

/** The job row's excluded files, grouped by the ready ZIP that holds them (card names, not storage ids). */
export function excludedBySource(excluded: readonly BulkZipExcludedFile[] | null | undefined): Array<{ sourceJobId: string | null; label: string; names: string[] }> {
    const groups = new Map<string, { sourceJobId: string | null; label: string; names: string[] }>();
    for (const e of excluded || []) {
        const key = e.sourceJobId ?? '';
        const g = groups.get(key) || { sourceJobId: e.sourceJobId, label: e.sourceJobLabel ? `${e.sourceJobLabel}.zip` : 'another ready ZIP', names: [] };
        g.names.push(e.fileName || 'a file');
        groups.set(key, g);
    }
    return [...groups.values()];
}

/**
 * The line under the ZIP button when starting one failed -- say WHICH thing went wrong ("Try again" is actively misleading
 * for the rate-limit case). F3r (review 7): no session (the anonymous sign-in gave up on 429) is its own case -- the ZIP is
 * the one identity-bound action on the page; the files themselves still download.
 */
export function bulkZipErrorText(err: unknown): string {
    if (err instanceof IdentityUnavailableError) return `${err.message} You can still download the files individually.`;
    const status = (err as any)?.status ?? (err as any)?.response?.status;
    return status === 429
        ? 'You already have several ZIPs being prepared. Wait for one to finish, then try again.'
        : status === 404
            ? 'Some of these files are no longer available to zip. Download them individually.'
            : 'We couldn\'t start the ZIP. Download the files individually, or try again.';
}
